import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { InputField } from "./InputAndSelectField";
import toast from "react-hot-toast";

interface Step2Props {
  data: Record<string, any>;
  onUpdate: (updated: Record<string, any>) => void;
}

const Step2EmployeePosition: React.FC<Step2Props> = ({ data, onUpdate }) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    onUpdate({ [name]: value });
  };

  useEffect(() => {
    if (data.dateOfJoining && data.probationDays) {
      const doj = new Date(data.dateOfJoining);
      const days = parseInt(data.probationDays);
      if (!isNaN(doj.getTime()) && !isNaN(days)) {
        const newConfirmationDate = new Date(doj);
        newConfirmationDate.setDate(doj.getDate() + days);
        const newConfirmationISO = newConfirmationDate.toISOString();
        // Only update if different to avoid infinite loops
        if (data.dateOfConfirmation !== newConfirmationISO) {
          onUpdate({ dateOfConfirmation: newConfirmationISO });
        }
      }
    }
  }, [data.dateOfJoining, data.probationDays]);

  const attendanceMetadata = useSelector(
    (state: RootState) => state.attendance.attendanceMetadata
  );

  const {
    departments = [],
    designations = [],
    locations = [],
    weekendPolicies = [],
    employmentStatuses = [],
  } = attendanceMetadata || {};

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
        Employee Position Details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <InputField
          label="Date Of Joining *"
          name="dateOfJoining"
          type="date"
          value={data.dateOfJoining}
          onChange={handleChange}
          required
        />
        <SelectField
          label="Department *"
          name="department"
          value={data.department}
          options={departments}
          onChange={handleChange}
        />
        <SelectField
          label="Designation *"
          name="designation"
          value={data.designation}
          options={designations}
          onChange={handleChange}
        />
        <SelectField
          label="Location *"
          name="location"
          value={data.location}
          options={locations}
          onChange={handleChange}
        />
        <SelectField
          label="Weekend Policy *"
          name="weekendPolicy"
          value={data.weekendPolicy}
          options={weekendPolicies}
          onChange={handleChange}
        />
        <SelectField
          label="Employment Status *"
          name="employmentStatus"
          value={data.employmentStatus}
          options={employmentStatuses}
          onChange={handleChange}
        />

        {/* Show "On Probation Days" only if "probation" is selected */}
        {data.employmentStatus === "probation" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                On Probation Days *
              </label>
              <input
                type="number"
                name="probationDays"
                value={data.probationDays || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (parseInt(value) < 0) {
                    toast.error("Probation days cannot be negative");
                    return;
                  }
                  onUpdate({ probationDays: value });

                  // Auto-update dateOfConfirmation if value is valid
                  const days = parseInt(value);
                  if (data.dateOfJoining) {
                    const doj = new Date(data.dateOfJoining);
                    if (!isNaN(doj.getTime())) {
                      const confirmation = new Date(doj);
                      confirmation.setDate(doj.getDate() + days);
                      onUpdate({
                        dateOfConfirmation: confirmation.toISOString(),
                      });
                    } else {
                      toast.error("Invalid Date of Joining");
                    }
                  } else {
                    toast.error("Please select Date of Joining first");
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 60"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmation Date *
              </label>
              <input
                type="date"
                name="dateOfConfirmation"
                value={
                  data.dateOfConfirmation
                    ? new Date(data.dateOfConfirmation)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  const selectedDate = new Date(value);
                  const joiningDate = new Date(data.dateOfJoining);

                  if (selectedDate < joiningDate) {
                    toast.error(
                      "Confirmation date cannot be before Date of Joining"
                    );
                    return;
                  }

                  const isoString = selectedDate.toISOString();

                  onUpdate({ dateOfConfirmation: isoString });

                  if (data.dateOfJoining) {
                    const doj = new Date(data.dateOfJoining);
                    const selectedDate = new Date(value);
                    if (
                      !isNaN(doj.getTime()) &&
                      !isNaN(selectedDate.getTime())
                    ) {
                      const diffTime = selectedDate.getTime() - doj.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
                      if (!isNaN(diffDays)) {
                        onUpdate({ probationDays: diffDays.toString() });
                      }
                    } else {
                      toast.error(
                        "Invalid Date of Joining or Confirmation Date"
                      );
                    }
                  } else {
                    toast.error("Please select Date of Joining first");
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        )}
        <InputField
          label="Previous Company Name *"
          name="previousCompanyName"
          value={data.previousCompanyName}
          onChange={handleChange}
          required
        />
        <InputField
          label="Previous Company Designation *"
          name="previousCompanyDesignation"
          value={data.previousCompanyDesignation}
          onChange={handleChange}
          required
        />
        <InputField
          label="Previous Company Experience (Years) *"
          type="number"
          name="previousCompanyExperience"
          value={data.previousCompanyExperience}
          onChange={handleChange}
          required
        />
      </div>
    </div>
  );
};

const SelectField = ({
  label,
  name,
  value,
  options,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  options: string[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1 tracking-wide">
      {label}
    </label>
    <select
      name={name}
      value={value || ""}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 bg-gray-50 rounded-md shadow-sm 
        focus:outline-none focus:ring-2 focus:ring-blue-500 
        transition duration-200 ease-in-out hover:border-gray-400"
    >
      <option value="">Select</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

export default Step2EmployeePosition;
